import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { isCustomDomain as checkIsCustomDomain } from '../../utils/routeHandler';
import { getThemeStyles } from '../../styles/themes';
import { loadGoogleFont, getFontFamily } from '../../utils/fontLoader';
import { deriveComponentColors, isGlobalPalette } from '../../utils/deriveComponentColors';
import { BlogPostView } from './BlogPostView';
import { BlogListView } from './BlogListView';
import { BlogLayoutWrapper } from './BlogLayoutWrapper';
import type {
  BlogConfig,
  BlogPost,
  Pagination,
  ThemeInfo,
  PostNavigation,
  BlogThemeStyles,
  BlogCustomColors,
  PublicSiteSummary,
  SiteNavigationItem,
  PublicBlogLayout,
} from './types';

// Get API base URL
const getApiBaseUrl = () => {
  // Use relative path for API calls (Vercel rewrites will proxy to the API)
  return '';
};

export const PublicBlogViewer: React.FC = () => {
  const { slug, tag } = useParams<{ slug?: string; tag?: string }>();
  const isCustomDomain = checkIsCustomDomain();

  // Extract identifier (subdomain or custom domain)
  const identifier = (() => {
    const hostname = window.location.hostname;
    if (isCustomDomain) {
      return hostname;
    }
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return parts[0];
    }
    return '';
  })();

  const [blog, setBlog] = useState<BlogConfig | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [singlePost, setSinglePost] = useState<BlogPost | null>(null);
  const [navigation, setNavigation] = useState<PostNavigation | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [tags, setTags] = useState<Array<{ tag: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [themeInfo, setThemeInfo] = useState<ThemeInfo | null>(null);
  const [site, setSite] = useState<PublicSiteSummary | null>(null);
  const [siteNavigation, setSiteNavigation] = useState<SiteNavigationItem[]>([]);
  const [pageLayout, setPageLayout] = useState<PublicBlogLayout | null>(null);
  const [showBranding, setShowBranding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Get brand fonts
  const brandFonts = useMemo(() => {
    return themeInfo?.fonts || null;
  }, [themeInfo?.fonts]);

  // Load Google fonts when they change
  useEffect(() => {
    if (brandFonts?.headingFont) {
      loadGoogleFont(brandFonts.headingFont);
    }
    if (brandFonts?.bodyFont && brandFonts.bodyFont !== brandFonts.headingFont) {
      loadGoogleFont(brandFonts.bodyFont);
    }
  }, [brandFonts?.headingFont, brandFonts?.bodyFont]);

  // Compute blog theme styles using unified theme system
  const theme: BlogThemeStyles = useMemo(() => {
    const themeName = themeInfo?.name || 'standard';

    // Build custom colors for the blog component
    let websiteThemeBlogColors = themeInfo?.customColors?.blog as BlogCustomColors | undefined;
    const blogSpecificColors = blog?.customColors;

    // If blog colors don't exist but _palette does, derive blog colors from the palette
    if (!websiteThemeBlogColors && themeInfo?.customColors?._palette) {
      const palette = themeInfo.customColors._palette;
      // Check if it's a valid global palette
      if (isGlobalPalette(palette)) {
        const allDerivedColors = deriveComponentColors(palette);
        websiteThemeBlogColors = allDerivedColors.blog as BlogCustomColors;
      }
    }

    // Merge custom colors: website theme blog colors + blog-specific colors
    const mergedBlogCustomColors: BlogCustomColors = {
      ...websiteThemeBlogColors,
      ...blogSpecificColors,
    };

    // Get the full theme styles with any custom overrides
    const customColors: Record<string, Record<string, string>> = {};
    if (Object.keys(mergedBlogCustomColors).length > 0) {
      customColors.blog = mergedBlogCustomColors as Record<string, string>;
    }

    // Get theme styles from unified system
    const themeStyles = getThemeStyles(
      themeName,
      Object.keys(customColors).length > 0 ? customColors : undefined
    );

    // Return the blog section with defaults
    return (themeStyles.blog || getThemeStyles('standard').blog) as BlogThemeStyles;
  }, [themeInfo, blog?.customColors]);

  const applySiteContext = (payload: {
    site?: PublicSiteSummary;
    siteNavigation?: SiteNavigationItem[];
    layout?: PublicBlogLayout | null;
    showBranding?: boolean;
    theme?: ThemeInfo;
  }) => {
    if (payload.site) setSite(payload.site);
    if (payload.siteNavigation !== undefined) setSiteNavigation(payload.siteNavigation);
    if (payload.layout !== undefined) setPageLayout(payload.layout);
    if (payload.showBranding !== undefined) setShowBranding(payload.showBranding);
    if (payload.theme) setThemeInfo(payload.theme);
  };

  const wrapInLayout = Boolean(pageLayout?.components?.length);

  const layoutTheme = useMemo(() => {
    if (!themeInfo) return null;
    return {
      name: themeInfo.name,
      ...themeInfo.customColors,
      fonts: themeInfo.fonts,
    };
  }, [themeInfo]);

  // Font styles for blog
  const fontStyles = useMemo(() => {
    return {
      headingFont: brandFonts?.headingFont
        ? { fontFamily: getFontFamily(brandFonts.headingFont) }
        : {},
      bodyFont: brandFonts?.bodyFont ? { fontFamily: getFontFamily(brandFonts.bodyFont) } : {},
    };
  }, [brandFonts]);

  // Determine view mode
  const viewMode = slug ? 'post' : tag ? 'tag' : 'list';

  useEffect(() => {
    loadBlogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, slug, tag, currentPage, isCustomDomain, activeSearch]);

  const loadBlogData = async () => {
    if (!identifier) return;

    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = getApiBaseUrl();

      if (viewMode === 'post' && slug) {
        // Load single post
        const postUrl = isCustomDomain
          ? `${apiBaseUrl}/api/website/by-domain/${identifier}/blog/${slug}`
          : `${apiBaseUrl}/api/website/${identifier}/blog/${slug}`;

        const response = await fetch(postUrl);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Post not found');
            return;
          }
          throw new Error(`Failed to fetch post: ${response.status}`);
        }

        const data = await response.json();
        const postData = data.success ? data.data : data;
        setBlog(postData.blog);
        setSinglePost(postData.post);
        setNavigation(postData.navigation || null);
        applySiteContext(postData);
      } else {
        // Load blog list
        let blogUrl = isCustomDomain
          ? `${apiBaseUrl}/api/website/by-domain/${identifier}/blog`
          : `${apiBaseUrl}/api/website/${identifier}/blog`;

        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        if (tag) {
          params.append('tag', tag);
        }
        if (activeSearch) {
          params.append('search', activeSearch);
        }
        blogUrl += `?${params.toString()}`;

        const response = await fetch(blogUrl);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Blog not found or not published');
            return;
          }
          throw new Error(`Failed to fetch blog: ${response.status}`);
        }

        const data = await response.json();
        const blogData = data.success ? data.data : data;
        setBlog(blogData.blog);
        setPosts(blogData.posts);
        setPagination(blogData.pagination);
        if (blogData.tags) {
          setTags(blogData.tags);
        }
        applySiteContext(blogData);
      }
    } catch (err) {
      console.error('Error loading blog:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: theme.pageBackground }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: theme.loadingSpinnerColor }}
          />
          <p style={{ color: theme.textColor }}>Loading blog...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !blog) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: theme.pageBackground }}
      >
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: theme.errorIconColor }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4" style={{ color: theme.titleColor }}>
            Blog Not Available
          </h1>
          <p className="mb-6" style={{ color: theme.textColor }}>
            {error || 'The blog could not be found or is not published.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors"
            style={{
              backgroundColor: theme.errorButtonBackground,
              color: theme.errorButtonText,
            }}
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  // Single post view
  if (viewMode === 'post' && singlePost) {
    return (
      <BlogLayoutWrapper
        site={site}
        siteNavigation={siteNavigation}
        pageLayout={pageLayout}
        theme={layoutTheme}
        showBranding={showBranding}
      >
        <BlogPostView
          blog={blog}
          post={singlePost}
          navigation={navigation}
          theme={theme}
          fontStyles={fontStyles}
        />
      </BlogLayoutWrapper>
    );
  }

  // Blog list view
  return (
    <BlogLayoutWrapper
      site={site}
      siteNavigation={siteNavigation}
      pageLayout={pageLayout}
      theme={layoutTheme}
      showBranding={showBranding}
    >
      <BlogListView
        blog={blog}
        posts={posts}
        pagination={pagination}
        tags={tags}
        theme={theme}
        fontStyles={fontStyles}
        wrapInLayout={wrapInLayout}
        currentPage={currentPage}
        selectedTag={tag}
        searchQuery={searchQuery}
        activeSearch={activeSearch}
        onPageChange={page => setCurrentPage(page)}
        onSearchChange={query => setSearchQuery(query)}
        onSearchSubmit={() => {
          setActiveSearch(searchQuery);
          setCurrentPage(1);
        }}
        onClearSearch={() => {
          setSearchQuery('');
          setActiveSearch('');
          setCurrentPage(1);
        }}
      />
    </BlogLayoutWrapper>
  );
};

export default PublicBlogViewer;
